import React, { useState, useEffect } from 'react';
import { db, auth } from '../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, User } from 'firebase/auth';
import { Database, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';

const MigrasiKeSupabaseView: React.FC = () => {
  const [logs, setLogs] = useState<string[]>([]);
  const [isMigrating, setIsMigrating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsub();
  }, []);

  const loginFirebase = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Login failed", error);
      alert("Login Firebase gagal.");
    }
  };

  const addLog = (msg: string) => {
    setLogs((prev) => [...prev, `${new Date().toLocaleTimeString()} - ${msg}`]);
  };

  const pushToSupabase = async (endpoint: string, items: any[], collectionsCount: number) => {
    let completed = 0;
    if (items.length === 0) return;

    // Split items into chunks to avoid large payloads
    const chunkSize = 50;
    for (let i = 0; i < items.length; i += chunkSize) {
      const chunk = items.slice(i, i + chunkSize);
      try {
        const res = await fetch(`/api/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(chunk)
        });
        
        if (!res.ok) {
          const errText = await res.text();
          addLog(`⚠️ Gagal mengirim chunk ke ${endpoint}: ${errText}`);
        } else {
          // addLog(`  - Chunk terkirim (${i}-${Math.min(i + chunkSize, items.length)})`);
        }
      } catch (error) {
        addLog(`❌ Kesalahan saat mengirim ke ${endpoint}: ${error}`);
      }
      completed += chunk.length;
      setProgress((prev) => prev + (chunk.length / items.length) * (100 / collectionsCount));
    }
  };

  const handleMigration = async () => {
    if (!window.confirm("Apakah Anda yakin ingin memindahkan data dari Firebase ke Supabase? Data di Supabase akan di-update (upsert).")) return;
    setIsMigrating(true);
    setLogs([]);
    setProgress(0);
    try {
      const collections = [
        { key: 'teachers', endpoint: 'teachers' },
        { key: 'students', endpoint: 'students' },
        { key: 'agendas', endpoint: 'agendas' },
        { key: 'attendanceRecords', endpoint: 'attendance' },
        { key: 'assessmentGrades', endpoint: 'assessment-grades' },
        { key: 'sasGrades', endpoint: 'sas-grades' },
        { key: 'formativeGrades', endpoint: 'formative-grades' },
        { key: 'learningObjectives', endpoint: 'objectives' },
        { key: 'schedules', endpoint: 'schedules' },
        { key: 'schoolSettings', endpoint: 'settings' },
        { key: 'tugas', endpoint: 'tugas' },
        { key: 'tugasSubmissions', endpoint: 'tugas-submissions' },
        { key: 'tugasSessions', endpoint: 'tugas-sessions' },
        { key: 'courseModules', endpoint: 'course-modules' },
        { key: 'courseSubmissions', endpoint: 'course-submissions' },
        { key: 'notifications', endpoint: 'notifications' },
        { key: 'profileAccessLogs', endpoint: 'profile-access-logs' },
        { key: 'learningContents', endpoint: 'learning-content' },
      ];

      for (let i = 0; i < collections.length; i++) {
        const col = collections[i];
        addLog(`Membaca data ${col.key} dari Firestore...`);
        
        try {
          const snap = await getDocs(collection(db, col.key));
          const rawItems = snap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
          
          // Data cleaning/formatting for Prisma
          const items = rawItems.map(item => {
            const newItem = { ...item };
            // Hande Firestore Timestamp objects if they exist
            Object.keys(newItem).forEach(key => {
              const val = newItem[key];
              if (val && typeof val === 'object' && val.seconds !== undefined && val.nanoseconds !== undefined) {
                newItem[key] = new Date(val.seconds * 1000).toISOString();
              }
            });
            return newItem;
          });
          
          if (items.length > 0) {
            addLog(`Mengirim ${items.length} data ke Supabase API: /api/${col.endpoint}...`);
            await pushToSupabase(col.endpoint, items, collections.length);
            addLog(`✅ Berhasil memindahkan ${col.key}.`);
          } else {
            addLog(`ℹ️ Tidak ada data di Firestore untuk ${col.key}. Lanjut...`);
            setProgress(((i + 1) / collections.length) * 100);
          }
        } catch (err) {
          addLog(`❌ Gagal membaca ${col.key} dari Firestore: ${err}`);
        }
      }

      setProgress(100);
      addLog("🚀 MIGRASI KE SUPABASE SELESAI!");
      alert("Data dari Firebase telah berhasil disinkronkan ke Supabase (PostgreSQL)!");
    } catch (e: any) {
      addLog(`❌ TERJADI KESALAHAN FATAL: ${e.message}`);
    }
    setIsMigrating(false);
  };

  return (
    <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 mt-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
          <Database size={24} />
        </div>
        <h3 className="text-xl font-bold text-slate-800">Sinkronisasi Ke Supabase (Cloud SQL)</h3>
      </div>
      
      <p className="text-sm text-slate-500 mb-6">
        Gunakan menu ini untuk memindahkan data terbaru yang ada di Firebase kembali ke database PostgreSQL (Supabase/Internal).
        Ini akan melakukan <strong>Upsert</strong> (memasukkan data baru atau memperbarui data lama jika ID sama).
      </p>

      {!user ? (
        <div className="mb-6 bg-amber-50 p-4 rounded-xl border border-amber-200">
          <p className="text-amber-700 font-bold mb-2">Anda perlu login ke Firebase!</p>
          <p className="text-amber-600 text-sm mb-4">Untuk membaca data yang akan dipindahkan, login terlebih dahulu.</p>
          <button 
            onClick={loginFirebase}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition"
          >
            Login dengan Google
          </button>
        </div>
      ) : (
        <div className="mb-6 bg-emerald-50 p-4 rounded-xl border border-emerald-200 flex items-center justify-between">
          <div>
            <p className="text-emerald-700 font-bold">Terhubung ke Firestore</p>
            <p className="text-emerald-600 text-xs">{user.email}</p>
          </div>
          <CheckCircle2 className="text-emerald-500" size={20} />
        </div>
      )}

      <button 
        className="w-full sm:w-auto px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-md shadow-indigo-200 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
        disabled={isMigrating || !user}
        onClick={handleMigration}
      >
        {isMigrating ? (
          <>
            <RefreshCw size={20} className="animate-spin" />
            Sedang Sinkronisasi...
          </>
        ) : (
          <>
            <Database size={20} />
            Mulai Sinkronisasi Data ke Supabase
          </>
        )}
      </button>

      {isMigrating && (
        <div className="mt-8">
          <div className="flex justify-between text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
            <span>Progress Migrasi</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
            <div 
              className="bg-indigo-600 h-3 rounded-full transition-all duration-500 ease-out" 
              style={{ width: `${progress}%` }} 
            />
          </div>
        </div>
      )}

      <div className="mt-8">
        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Log Sinkronisasi</h4>
        <div className="p-4 bg-slate-900 text-emerald-400 font-mono text-[10px] rounded-2xl h-48 overflow-y-auto w-full border border-slate-800 shadow-inner">
          {logs.length === 0 ? (
            <span className="text-slate-600 italic">Siap memulai sinkronisasi...</span>
          ) : (
            logs.map((l, i) => <div key={i} className="mb-1">{l}</div>)
          )}
        </div>
      </div>
    </div>
  );
};

export default MigrasiKeSupabaseView;
